import PlaceholderComponent, { type PlaceholderProps } from '@app/components/placeholder';
import { loremIpsum } from 'lorem-ipsum';
import type * as React from 'react';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';

//#region : Global constants and types

/** Custom. Used to store the canvas element to draw the placeholder. */
let canvasEl: HTMLCanvasElement | null = null;

/** Custom function. Creates and returns a canvas element. */
const getCanvas = () => {
    return canvasEl || (canvasEl = document.createElement('canvas'));
};

/** Custom. Generated lorem ipsum text.  */
const Lorem = loremIpsum({
    count: 6, //no. of paragraphs
    format: 'plain',
    paragraphLowerBound: 2, // Min. number of sentences per paragraph.
    paragraphUpperBound: 4, // Max. number of sentences per paragarph.
    suffix: '\n\n', // Line ending
    units: 'paragraphs',
});
//#endregion : Global constants and types

//#region : Main component

/** Custom. Main component parameters type */
export type LipsumPlaceholderProps = {
    containerClassName?: string;
    className?: string;
    fontSize: number;
    lineHeight?: number;
    item: any;
    children?: React.ReactNode;
};

/**
 * Custom helper component.
 *
 * Shows multiline / multiparagraph text placeholder.
 */
export default function LipsumPlaceholder({
    containerClassName,
    className,
    fontSize,
    item,
    children,
}: LipsumPlaceholderProps) {
    const placeholderRef = useRef<HTMLDivElement>(null);
    const lastItem = useRef(item);

    const [widths, setWidths] = useState<number[]>([]);

    //Calculates width of each lorem-ipsum line and stores in the widths array state.
    //Later we iterate over each line and get it's corresponding width.
    //We show the placeholder for that line of that width.
    useEffect(() => {
        const el = placeholderRef.current;

        if (!el || item === lastItem) {
            return;
        }

        lastItem.current = item;

        const canvas = getCanvas();

        const context = canvas.getContext('2d')!;
        context.font = `${fontSize || 16}px Open Sans`;

        const widths: number[] = [];

        for (const line of Lorem.split('\n')) {
            let words = '';
            let width = 0;

            for (const word of line.split(' ')) {
                words = `${words} ${word}`;

                const measured = context.measureText(words);

                if (!measured) {
                    continue;
                }

                if (width + measured.width >= el.clientWidth) {
                    widths.push(width);

                    words = '';
                    width = 0;
                } else {
                    width += measured.width;
                }
            }

            if (words.trim().length > 0) {
                widths.push(width);
            }

            if (line.length === 0) {
                widths.push(0);
            }
        }

        startTransition(() => setWidths(widths));
    }, [fontSize, item]);

    //Iterate over each width and create an array of placeholders.
    const placeholderProps = useMemo<Omit<PlaceholderProps, 'item'>>(() => {
        return {
            count: widths.length,
            skeletonProps: idx => ({
                animation: 'wave',
                style: { marginBottom: widths[idx] === 0 ? 8 : 4 },
                height: 30,
                width: widths[idx],
            }),
        };
    }, [widths]);

    return (
        <div className={containerClassName} ref={placeholderRef}>
            <PlaceholderComponent className={className} item={item} {...placeholderProps}>
                {children}
            </PlaceholderComponent>
        </div>
    );
}
//#endregion : Main component
